'use client';

import type { Tables } from '@root/database';
import { Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { createTournament, updateTournament } from '@/lib/tournaments/actions';
interface TournamentDialogProps {
  games: Tables<'game'>[];
  tournament?: {
    game: Tables<'game'> | null;
  } & Tables<'tournaments'>;
}
export const TournamentDialog = ({
  games,
  tournament
}: TournamentDialogProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    game_id: tournament?.game_id || '',
    is_public: tournament?.is_public || false,
    location: tournament?.location || '',
    name: tournament?.name || '',
    starts_at: tournament?.starts_at ? new Date(tournament.starts_at).toISOString().slice(0, 16) : ''
  });
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      const data = {
        game_id: formData.game_id || null,
        is_public: formData.is_public,
        location: formData.location || null,
        name: formData.name,
        starts_at: new Date(formData.starts_at).toISOString()
      };
      if (tournament) {
        await updateTournament(tournament.id, data);
      } else {
        await createTournament(data);
      }
      setOpen(false);
      setFormData({
        game_id: '',
        is_public: false,
        location: '',
        name: '',
        starts_at: ''
      });
      toast.success(tournament ? 'Tournoi modifié' : 'Tournoi créé');
    } catch (error) {
      console.error(error);
      toast.error('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };
  return <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {tournament ? <Button aria-label="Modifier le tournoi" className="hover:bg-gray-100" size="icon" title="Modifier le tournoi" variant="ghost">
            <Pencil className="h-4 w-4 text-gray-700" />
          </Button> : <Button>
            <Plus />
            Nouveau tournoi
          </Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {tournament ? 'Modifier le tournoi' : 'Nouveau tournoi'}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="name">Nom du tournoi</Label>
            <Input id="name" required value={formData.name} onChange={event_0 => setFormData({
            ...formData,
            name: event_0.target.value
          })} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="game">Jeu</Label>
            <Select value={formData.game_id} onValueChange={value => setFormData({
            ...formData,
            game_id: value
          })}>
              <SelectTrigger id="game">
                <SelectValue placeholder="Sélectionner un jeu" />
              </SelectTrigger>
              <SelectContent>
                {games.map(game => <SelectItem key={game.id} value={game.id}>
                    {game.name} ({game.console})
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Lieu</Label>
            <Input id="location" value={formData.location} onChange={event_1 => setFormData({
            ...formData,
            location: event_1.target.value
          })} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="starts_at">Date de début</Label>
            <Input id="starts_at" required type="datetime-local" value={formData.starts_at} onChange={event_2 => setFormData({
            ...formData,
            starts_at: event_2.target.value
          })} />
          </div>

          <div className="flex items-center space-x-2">
            <input id="is_public" checked={formData.is_public} className="h-4 w-4" type="checkbox" onChange={event_3 => setFormData({
            ...formData,
            is_public: event_3.target.checked
          })} />
            <Label htmlFor="is_public">Public</Label>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button disabled={loading} type="submit">
              {loading && <Spinner />}
              {tournament ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>;
};